import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import { UI_CONTRACT_V1 } from "@gordonland/ui";
import type {
  ActiveControlSchemeV1,
  ControlInputCapabilitiesV1,
  ControlSchemePreferenceLoadStateV1,
  ControlSchemePreferenceV1,
} from "@gordonland/ui";

const DATABASE_NAME = "hearthwild-device-preferences";
const DATABASE_VERSION = 1;
const STORE_NAME = "preferences";
const CONTROL_SCHEME_KEY = "control-scheme";

interface DevicePreferencesDatabaseV1 extends DBSchema {
  readonly preferences: {
    readonly key: string;
    readonly value: unknown;
  };
}

interface StoredControlSchemePreferenceV1 {
  readonly firstEntrySelectionComplete: true;
  readonly preference: ControlSchemePreferenceV1;
  readonly version: 1;
}

export interface ControlSchemeSwitchPlanAppV1 {
  readonly activeScheme: ActiveControlSchemeV1;
  readonly announcement: string;
  readonly preference: ControlSchemePreferenceV1;
}

export interface ControlSchemePreferenceRepositoryV1 {
  load(): Promise<ControlSchemePreferenceLoadStateV1>;
  save(preference: ControlSchemePreferenceV1): Promise<void>;
  clear(): Promise<void>;
  writeRawForQa(value: unknown): Promise<void>;
}

function isPreference(value: unknown): value is ControlSchemePreferenceV1 {
  return value === "auto" || value === "keyboard_mouse" || value === "touch";
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

function unsupportedState(
  reason: Extract<ControlSchemePreferenceLoadStateV1, { kind: "unselected" }>["reason"],
): ControlSchemePreferenceLoadStateV1 {
  return Object.freeze({
    firstEntrySelectionComplete: false,
    kind: "unselected",
    preference: null,
    reason,
    requiredView: "first-entry",
  });
}

function parseStoredPreference(value: unknown): ControlSchemePreferenceLoadStateV1 {
  if (value === undefined) return unsupportedState("missing");
  if (!isRecord(value)) return unsupportedState("corrupt");
  if (value["version"] !== 1) return unsupportedState("unknown_version");
  if (!isPreference(value["preference"])) return unsupportedState("unknown_value");
  if (value["firstEntrySelectionComplete"] !== true) return unsupportedState("corrupt");
  return Object.freeze({
    firstEntrySelectionComplete: true,
    kind: "selected",
    preference: value["preference"],
  });
}

async function database(): Promise<IDBPDatabase<DevicePreferencesDatabaseV1>> {
  return openDB<DevicePreferencesDatabaseV1>(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(connection) {
      if (!connection.objectStoreNames.contains(STORE_NAME)) connection.createObjectStore(STORE_NAME);
    },
  });
}

export function createControlSchemePreferenceRepositoryV1(): ControlSchemePreferenceRepositoryV1 {
  return Object.freeze({
    async load(): Promise<ControlSchemePreferenceLoadStateV1> {
      const connection = await database();
      try {
        return parseStoredPreference(await connection.get(STORE_NAME, CONTROL_SCHEME_KEY));
      } finally {
        connection.close();
      }
    },
    async save(preference: ControlSchemePreferenceV1): Promise<void> {
      const connection = await database();
      const value: StoredControlSchemePreferenceV1 = Object.freeze({
        firstEntrySelectionComplete: true,
        preference,
        version: 1,
      });
      try {
        await connection.put(STORE_NAME, value, CONTROL_SCHEME_KEY);
      } finally {
        connection.close();
      }
    },
    async clear(): Promise<void> {
      const connection = await database();
      try {
        await connection.delete(STORE_NAME, CONTROL_SCHEME_KEY);
      } finally {
        connection.close();
      }
    },
    async writeRawForQa(value: unknown): Promise<void> {
      const connection = await database();
      try {
        await connection.put(STORE_NAME, value, CONTROL_SCHEME_KEY);
      } finally {
        connection.close();
      }
    },
  });
}

export function discoverControlInputCapabilitiesV1(): ControlInputCapabilitiesV1 {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return Object.freeze({ coarsePointer: false, finePointerWithHover: true, keyboard: true, mixedInputAvailable: false, touch: false });
  }
  const qaProfile = new URLSearchParams(window.location.search).get("capability");
  if (new URLSearchParams(window.location.search).get("qa") === "p0-022") {
    if (qaProfile === "pure-touch") {
      return Object.freeze({ coarsePointer: true, finePointerWithHover: false, keyboard: false, mixedInputAvailable: false, touch: true });
    }
    if (qaProfile === "hybrid") {
      return Object.freeze({ coarsePointer: true, finePointerWithHover: true, keyboard: true, mixedInputAvailable: true, touch: true });
    }
    if (qaProfile === "pure-desktop") {
      return Object.freeze({ coarsePointer: false, finePointerWithHover: true, keyboard: true, mixedInputAvailable: false, touch: false });
    }
  }
  const finePointerWithHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const touch = navigator.maxTouchPoints > 0 || coarsePointer;
  const keyboard = finePointerWithHover;
  if (keyboard && finePointerWithHover && touch) {
    return Object.freeze({ coarsePointer, finePointerWithHover, keyboard, mixedInputAvailable: true, touch });
  }
  if (keyboard && finePointerWithHover) {
    return Object.freeze({ coarsePointer, finePointerWithHover, keyboard, mixedInputAvailable: false, touch: false });
  }
  return Object.freeze({ coarsePointer: true, finePointerWithHover: false, keyboard: false, mixedInputAvailable: false, touch: true });
}

export function supportsControlSchemeV1(
  capabilities: ControlInputCapabilitiesV1,
  preference: ControlSchemePreferenceV1,
): boolean {
  if (preference === "auto") return true;
  if (preference === "keyboard_mouse") return capabilities.keyboard && capabilities.finePointerWithHover;
  return capabilities.touch;
}

export function resolveActiveControlSchemeV1(
  capabilities: ControlInputCapabilitiesV1,
  preference: ControlSchemePreferenceV1,
  evidence?: ActiveControlSchemeV1,
): ActiveControlSchemeV1 {
  if (preference === "keyboard_mouse") return "keyboard_mouse";
  if (preference === "touch") return "touch";
  if (evidence !== undefined && supportsControlSchemeV1(capabilities, evidence)) return evidence;
  return capabilities.keyboard && capabilities.finePointerWithHover ? "keyboard_mouse" : "touch";
}

export function planControlSchemeSwitchV1(
  capabilities: ControlInputCapabilitiesV1,
  preference: ControlSchemePreferenceV1,
  evidence?: ActiveControlSchemeV1,
): ControlSchemeSwitchPlanAppV1 | null {
  if (!supportsControlSchemeV1(capabilities, preference)) return null;
  const activeScheme = resolveActiveControlSchemeV1(capabilities, preference, evidence);
  return Object.freeze({
    activeScheme,
    announcement: UI_CONTRACT_V1.controlSchemeCopyZhCn.switched[preference],
    preference,
  });
}

export const CONTROL_SCHEME_COPY_ZH_CN_V1 = UI_CONTRACT_V1.controlSchemeCopyZhCn;
export const CONTROL_SCHEME_CHOICES_ZH_CN_V1 = UI_CONTRACT_V1.controlSchemeChoicesZhCn;
